/**
 * funnelCanvasMap.ts — карта холста воронки, доводка 1 (WEB-3764): виды
 * нового экрана, окно «Вставить экран» / «Новый экран», плавающая панель
 * выделенного экрана, удаление с подтверждением, копия, переименование,
 * окно «Выйти из конструктора?».
 *
 * Эталон — `design/cabinet-rescin/sections/editor-c.jsx`: виды `scrTypes`
 * `:84` (en `:142`), `newScr`, `tbPh`, `tb.ai/dup/del`, `delQ`, `delD`, `del`,
 * `aiCancel`, `dupOk`, `dupLogic`, `rename` `:90-91` (en `:148-149`), `open`,
 * `leaveQ`, `leaveStay`, `leaveGo` `:92` (en `:150`). «Приветствие» — вид
 * стартового экрана (в окне не показывается). «Будет позже» — «Изменить с ИИ»
 * под флагом черновика (решение владельца 27.10 № 27). `editFailed` — отказ
 * записи правки этой доводки всплывашкой (п. 67). Заголовок «Вставить экран» —
 * общий ключ `funnelCanvas.insertScreen`, суффикс копии —
 * `editor.screenCopySuffix` (тот же, что у «Слоёв»).
 *
 * WEB-4013, линза «Логика» на карте — тексты эталона дословно: `logicHint`
 * `:89` (en `:147`), `linked`, `linkedNext` `:91` (`:149`), `portQ` `:92`
 * (`:150`), `fromWhere` и `issueOpen` (`x.openScr`) `:88` (`:146`),
 * `logicCheck` (`x.lg.check`) `:95` (`:153`), подсказка точки `portTip`
 * (`x.i.leads`) `:162`. Слова и сути меток — `funnelGraph.ts` (`mark*`).
 *
 * WEB-3767, линза «Логика», второй юнит (эталон b9a231c0): `ruleAns`
 * (`x.ans`) и `ruleElse` (`x.rest`) `:89` (en `:147`), `ruleBad` и `logicAll`
 * (`x.lg2`) `:195` (`:198`), окошко правила `rl*` (`x.rl`) `:74` (`:132`),
 * `ruleDone` `:37` (`:57`); `rlIfAny` — своё: голова правила без условия по
 * ответу («Если» + первое условие строкой окна логики).
 *
 * WEB-4086: `ruleAnsAny` (подпись стрелки «один из [A, B]») — своё;
 * `rlIsOneOf` (окошко правила у головы-списка) — эталон `rl.oneOf`
 * `editor-c.jsx:74` «одно из» (сверка вида 02.11), en — «is one of».
 *
 * WEB-4059: `dropToPaywall` / `dropFromPaywall` — строка у конца протяжки на
 * экран другой части; в эталоне её нет, текст — составителя задания пачки 53
 * (владелец 30.10 одобрил вид «одна строка, исчезает сама»).
 *
 * WEB-4088: блоки «Пройти как посетитель» и «Переменные» — `simTitle`,
 * `simGo`, `simReset`, `varsTitle` (`x.lg`) `:95` (`:153`), `simAnswer`
 * (`lg.answer`) там же, `varCopied` `:90` (`:148`); `toTarget`, `byOrder`
 * (`byOrderS`, метки «куда ведёт» у экрана) `:98` (`:156`); `simRow`,
 * `simNoForks`, `varsNone` — тексты задания WEB-4088 (в эталоне их нет).
 */
import { defineDictionary } from '../define';

/** «A», «B» или «C»: через запятую, последний — союзом. */
const listOr = (xs: string[], or: string) =>
  xs.length < 2
    ? xs.join('')
    : `${xs.slice(0, -1).join(', ')} ${or} ${xs[xs.length - 1]}`;

export const funnelCanvasMapStrings = defineDictionary(
  {
    kindChoice: 'Question',
    kindChoiceHint: 'One or several answer options',
    kindInfo: 'Info',
    kindInfoHint: 'Text and image between questions',
    kindReviews: 'Reviews',
    kindReviewsHint: 'Trust: reviews and ratings',
    kindEmail: 'Email',
    kindEmailHint: 'Collect email before the result',
    kindLoader: 'Loader',
    kindLoaderHint: 'A “building your plan” pause',
    kindPaywall: 'Paywall',
    kindPaywallHint: 'Plans and payment',
    kindBlank: 'Blank',
    kindBlankHint: 'Build it yourself from blocks',
    kindWelcome: 'Welcome',
    newScreen: 'New screen',
    open: 'Open screen',
    editAi: 'Edit with AI',
    askPlaceholder: 'What should change on this screen?',
    soon: 'Coming later',
    duplicate: 'Duplicate',
    remove: 'Delete',
    deleteQ: 'Delete the screen?',
    deleteD: 'Arrows will connect directly.',
    cancel: 'Cancel',
    dupOk: 'Screen duplicated — the copy is right after the original',
    dupLogic: (name: string) =>
      `Screen duplicated. Logic rules stay on “${name}”, the copy has none.`,
    rename: 'Rename',
    editFailed: 'Couldn’t save the change',
    leaveQ: 'Leave the builder?',
    leaveStay: 'Stay',
    leaveGo: 'Leave',
    // WEB-4013
    logicHint:
      'Drag from the dot by an answer or a screen to where it leads; drop on empty space for a new screen',
    linked: (name: string) => `Answer now leads to “${name}”`,
    linkedNext: (name: string) => `Next comes “${name}”`,
    portQ: (answer: string) => `Where does “${answer}” lead?`,
    portTip: 'Leads to',
    // WEB-4059: строка у конца протяжки на экран другой части
    dropToPaywall: 'The quiz leads to the paywall on its own — no link needed',
    dropFromPaywall: "The paywall can't link back to the quiz",
    fromWhere: 'Where from?',
    issueOpen: 'Open',
    logicCheck: 'Logic check',
    // WEB-3767 подписи стрелок и окошко правила
    ruleAns: (answer: string) => `Answer “${answer}”`,
    // WEB-4086: голова «один из» — все ответы
    ruleAnsAny: (answers: string[]) =>
      `Answer ${listOr(
        answers.map((a) => `“${a}”`),
        'or',
      )}`,
    ruleElse: 'Any other answer',
    ruleBad: 'rule has an error',
    logicAll: 'All rules',
    rlIf: 'If the answer to',
    rlRestIf: 'Any other answer to',
    rlIfAny: 'If',
    rlIs: 'is',
    rlIsOneOf: 'is one of',
    rlAns: 'Answer',
    rlAnd: 'and',
    rlGo: 'Where',
    rlAddCond: 'Condition',
    ruleDone: 'Done',
    // WEB-4088: «Пройти как посетитель» и «Переменные» (эталон :95, :90, :98)
    simTitle: 'Walk as a visitor — their answers',
    simRow: (n: number, question: string) => `${n}. “${question}”`,
    simAnswer: (question: string) => `Answer to “${question}”`,
    simGo: 'Walk',
    simReset: 'Reset',
    simNoForks: 'The funnel has no branches — one path',
    varsTitle: 'Variables',
    varsNone: 'The funnel has no variables',
    varCopied: (v: string) => `{{${v}}} copied — paste it into a text`,
    toTarget: '→ ',
    byOrder: 'in order',
    // WEB-2438 Ж14 ч.2: имя нового пустого экрана — по языку кабинета.
    screenDefaultName: (n: number) => `Screen ${n}`,
  },
  {
    kindChoice: 'Вопрос',
    kindChoiceHint: 'Один или несколько вариантов ответа',
    kindInfo: 'Информация',
    kindInfoHint: 'Текст и картинка между вопросами',
    kindReviews: 'Отзывы',
    kindReviewsHint: 'Доверие: отзывы и оценки',
    kindEmail: 'Почта',
    kindEmailHint: 'Сбор почты перед результатом',
    kindLoader: 'Загрузка',
    kindLoaderHint: 'Пауза «готовим ваш план»',
    kindPaywall: 'Пейволл',
    kindPaywallHint: 'Тарифы и оплата',
    kindBlank: 'Пустой',
    kindBlankHint: 'Собрать самому из блоков',
    kindWelcome: 'Приветствие',
    newScreen: 'Новый экран',
    open: 'Открыть экран',
    editAi: 'Изменить с ИИ',
    askPlaceholder: 'Что поменять на этом экране?',
    soon: 'Будет позже',
    duplicate: 'Дублировать',
    remove: 'Удалить',
    deleteQ: 'Удалить экран?',
    deleteD: 'Стрелки соединятся напрямую.',
    cancel: 'Отмена',
    dupOk: 'Экран скопирован — копия стоит сразу за исходным',
    dupLogic: (name: string) =>
      `Экран скопирован. Правила «Логики» остались на «${name}», у копии их нет.`,
    rename: 'Переименовать',
    editFailed: 'Не удалось сохранить правку',
    leaveQ: 'Выйти из конструктора?',
    leaveStay: 'Остаться',
    leaveGo: 'Выйти',
    // WEB-4013
    logicHint:
      'Потяните из точки у ответа или экрана туда, куда он ведёт; на пустом месте — новый экран',
    linked: (name: string) => `Ответ ведёт на «${name}»`,
    linkedNext: (name: string) => `Дальше — «${name}»`,
    portQ: (answer: string) => `Куда ведёт «${answer}»?`,
    portTip: 'Куда ведёт',
    dropToPaywall: 'Квиз сам ведёт в пейволл — связь не нужна',
    dropFromPaywall: 'Из пейволла в квиз связь не ставится',
    fromWhere: 'Откуда ведёт?',
    issueOpen: 'Открыть',
    logicCheck: 'Проверка логики',
    // WEB-3767 подписи стрелок и окошко правила
    ruleAns: (answer: string) => `Ответ «${answer}»`,
    ruleAnsAny: (answers: string[]) =>
      `Ответ ${listOr(
        answers.map((a) => `«${a}»`),
        'или',
      )}`,
    ruleElse: 'Любой другой ответ',
    ruleBad: 'правило с ошибкой',
    logicAll: 'Все правила',
    rlIf: 'Если ответ на',
    rlRestIf: 'Любой другой ответ на',
    rlIfAny: 'Если',
    rlIs: 'равен',
    rlIsOneOf: 'одно из',
    rlAns: 'Ответ',
    rlAnd: 'и',
    rlGo: 'Куда',
    rlAddCond: 'Условие',
    ruleDone: 'Готово',
    simTitle: 'Пройти как посетитель — что он ответил',
    simRow: (n: number, question: string) => `${n}. «${question}»`,
    simAnswer: (question: string) => `Ответ на «${question}»`,
    simGo: 'Пройти',
    simReset: 'Сбросить',
    simNoForks: 'В воронке нет развилок — путь один',
    varsTitle: 'Переменные',
    varsNone: 'В воронке нет переменных',
    varCopied: (v: string) => `{{${v}}} скопирована — вставьте в текст`,
    toTarget: '→ ',
    byOrder: 'по порядку',
    // WEB-2438 Ж14 ч.2: имя нового пустого экрана — по языку кабинета.
    screenDefaultName: (n: number) => `Экран ${n}`,
  },
);
